import { IDisposable } from "../../../base/common/lifecycle.js";
import { Emitter, Event } from "../../../base/common/events.js";
import { createDecorator } from "../../instantiation/common/registry.js";
import { CancellationToken } from "../../../base/common/cancellation.js";
import { IAttachment } from "../../../base/common/attachment.js";
import { ServiceCollection } from "../../instantiation/common/serviceCollection.js";
import { ServicesAccessor } from "../../instantiation/common/instantiation.js";

// #region Hierarchy Service

export const IHierarchyService = createDecorator<IHierarchyService>("hierarchyService");

export interface HierarchyChangedEvent {
    readonly hierarchy: IHierarchyService;
}
export type SelectedNodesChangedEvent = {
    readonly selection: IHierarchyNode[];
    /** Most recently acted-on node. Selection order is not interaction order. */
    readonly active?: IHierarchyNode;
}

export interface IHierarchyService {

    readonly _serviceBrand: undefined;

    readonly onDidChange: Event<HierarchyChangedEvent>;
    readonly onNodeAdded: Event<IHierarchyNode>;
    readonly onNodeRemoved: Event<IHierarchyNode>;
    readonly onNodeChanged: Event<IHierarchyNode>;
    readonly onSelectedNodesDidChange: Event<SelectedNodesChangedEvent>;

    notifyChanged: () => void;

    add(object: unknown, parent?: IHierarchyNode, token?: CancellationToken): Promise<IHierarchyNode | null>;
    remove(object: unknown): void;
    clear(): void;

    readonly roots: IHierarchyNode[];
    /** every registered node, roots and descendants — O(1), see the implementation */
    readonly nodeCount: number;

    getSelectedNodes(): IHierarchyNode[];
    find(object: unknown | null | undefined): IHierarchyNode | null;
    /** SESSION-INTERNAL lookup by the hierarchy node's own id (a three
     * `object.id` string) — UI plumbing between components that already share a
     * session (tree commands, icons). NEVER an addressing rung: the id means
     * nothing after a reload and collides across three instances. Anything that
     * names an object for the document, ops, or the AI boundary uses
     * `findAllByNeedleId` / the target resolver (plans/operation-bridge.md 1h). */
    findById(id: string): IHierarchyNode | null;
    /** EVERY registered holder of the PERSISTENT identity
     * (`userData["needle-id"]`), O(1) — the resolution primitive for
     * address-carrying operations. Identity is the PAIR (ref, id): the same
     * asset placed twice legitimately shares baked ids, so consumers pick the
     * in-scope holder (the target resolver) or fail loud on ambiguity — there
     * is deliberately NO singular lookup ("some holder" is the wrong-copy bug
     * class). Ids minted after registration are found via a rebuild-on-miss
     * backstop; empty when no registered node holds the id. */
    findAllByNeedleId(id: string): IHierarchyNode[];
    search(query: string | string[], maxResults?: number, token?: CancellationToken): Promise<IHierarchyNode[]>;

    /**
     * Iterate over all nodes in the hierarchy recursively
     */
    iterateNodes(): Iterable<IHierarchyNode>;

    /** Nodes between two rows in the currently visible, depth-first tree order. */
    getVisibleRange(from: IHierarchyNode, to: IHierarchyNode): IHierarchyNode[];

    /**
     * Navigate from the given node in the specified direction.
     * @param node The starting node for navigation.
     * @param direction The direction to navigate ('up', 'down', 'left', 'right').
     * @returns The node in the specified direction, or null if there is no such node.
     */
    navigate(direction: 'up' | 'down' | 'left' | 'right'): IHierarchyNode | null;
}

// #region Provider


export const ITreeProviderService = createDecorator<ITreeProviderService>('treeProviderService');

export interface ITreeProviderService {
    readonly _serviceBrand: undefined;

    registerProvider(provider: ITreeProvider): void;
    unregisterProvider(provider: ITreeProvider): void;
    getProvider(object: unknown): ITreeProvider | null;
}


export interface ITreeProvider {
    canParse(object: unknown): boolean;
    createTree(accessor: ServicesAccessor, object: unknown): Promise<IHierarchyNode | null>;
}





// #region Nodes

export const enum NodeFlags {
    /**
     * No special flags.
     */
    None = 0,
    /**
     * Node should be hidden in hierarchy view.
     */
    HideInHierarchy = 1 << 0,

    Inactive = 1 << 1,
}

export interface NodeChangedEvent {
    node: IHierarchyNode,
    added?: IHierarchyNode,
    removed?: IHierarchyNode,
};

export interface IHierarchyNode extends IDisposable {
    readonly id: string;
    /**
     * Optional source-object uuid (e.g. three.js object.uuid), when the
     * provider has one. NOT guaranteed unique or stable enough for keyed list
     * rendering — use {@link renderKey} for that.
     */
    readonly uuid?: string;
    /**
     * Guaranteed-unique key for keyed list rendering in the UI (Svelte
     * `{#each nodes as n (n.renderKey)}`). Generated once per node instance
     * and never reused: unlike `uuid`/`id` it cannot collide (numeric
     * provider ids collide across module instances — page three vs
     * editor-bundled three) or flip identity between updates, either of
     * which corrupts Svelte's keyed-each reconciliation ("reading 'prev'"
     * internal errors).
     */
    readonly renderKey: string;
    readonly parent: IHierarchyNode | null;
    readonly children: IHierarchyNode[];
    readonly owner: object;

    get hasChildren(): boolean;

    readonly onDidChange: Event<NodeChangedEvent>;
    notifyChanged(): void;
    setWithoutNotify<T extends keyof Pick<IHierarchyNode, "name" | "expanded" | "selected">>(key: T, value: IHierarchyNode[T]): void;

    flags: NodeFlags;
    type: string;
    icon?: string;
    name: string;
    label: string;
    selected: boolean;
    expanded: boolean;

    get visible(): boolean;
    get path(): string;

    readonly attachments: Array<IAttachment>;

    /**
     * Optional tags for searching or filtering nodes. These tags are not displayed in the UI.
     */
    searchTags: string[] | null | undefined;

    /**
     * Adds a child node at the specified index or at the end if no index is provided.
     * @param node The child node to add.
     * @param index Optional index at which to insert the child node. If not provided, the child is added at the end.
     */
    addChild(node: IHierarchyNode, index?: number): void;

    /**
     * Removes a child node.
     * @param node The child node to remove.
     */
    removeChild(node: IHierarchyNode): void;

    toString(): string;
}


export function isHierarchyNode(object: unknown): object is IHierarchyNode {
    return typeof object === 'object'
        && object !== null
        && 'id' in object
        && 'children' in object
        && 'flags' in object
}
